/**
 * DogAvatar — render pieska z 7 warstwami stroju + tryb kąpielowy (swim).
 * Używa kluczy tekstur z manifestu; brak → placeholdery proceduralne.
 * V2: setPose + layoutWithAnchors (AnimatedDogAvatar); publiczne API V1 bez zmian.
 */
import Phaser from 'phaser';
import { DOGS, type DogBreedId, type ItemDef, ITEMS } from '@pieskowo/shared';
import {
  parseOutfit,
  type OutfitSlots,
  type SwimSlots,
  emptyOutfitSlots,
  emptySwimSlots,
  itemTextureKey,
  type AvatarLayer,
} from './parseOutfit.js';
import {
  type AnchorPose,
  SLOT_ANCHOR,
  anchorToLocal,
  slotLocalOffset,
  shoesVisibleForPose,
} from './anchors.js';
import { ensureItemTexture } from './proceduralItemTexture.js';

export type DogAvatarOpts = {
  scene: Phaser.Scene;
  x: number;
  y: number;
  breed?: DogBreedId | string;
  color?: string;
  /** Skala wyświetlania (szafa ~2.2, park ~1). */
  scale?: number;
  mode?: 'normal' | 'swim';
  slots?: OutfitSlots;
  swimSlots?: SwimSlots;
  /** Depth bazowy kontenera. */
  depth?: number;
};

const ITEM_BY_ID = new Map(ITEMS.map((i) => [i.id, i]));

/** Kolory proceduralnych „ubranek" (placeholdery gdy brak atlasu). */
const SLOT_COLORS: Record<string, number> = {
  body: 0xff9ec8,
  hat: 0x7ec8e3,
  bow: 0xff6b6b,
  glasses: 0x4a3f55,
  collar: 0xffd700,
  shoes: 0xc4a1ff,
  accessory: 0xb8e6d0,
  suit: 0xff69b4,
  cap: 0x5dade2,
};

function parseColorHex(hex: string | undefined, fallback: number): number {
  if (!hex) return fallback;
  const s = hex.startsWith('#') ? hex.slice(1) : hex;
  const n = Number.parseInt(s, 16);
  return Number.isFinite(n) ? n : fallback;
}

export class DogAvatar {
  readonly container: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private breed: string;
  private colorHex: string;
  private mode: 'normal' | 'swim';
  private slots: OutfitSlots;
  private swimSlots: SwimSlots;
  private displayScale: number;
  private layerNodes = new Map<string, Phaser.GameObjects.GameObject>();
  /** Klatka bazy (idle/walkA/…); V2. */
  private pose = 'idle';
  private flipX = false;
  private anchors: AnchorPose | null = null;

  constructor(opts: DogAvatarOpts) {
    this.scene = opts.scene;
    this.breed = opts.breed ?? 'maltese';
    this.colorHex =
      opts.color ??
      (DOGS[this.breed as DogBreedId]?.defaultColor ?? '#FFF5EE');
    this.mode = opts.mode ?? 'normal';
    this.slots = opts.slots ? { ...opts.slots } : emptyOutfitSlots();
    this.swimSlots = opts.swimSlots ? { ...opts.swimSlots } : emptySwimSlots();
    this.displayScale = opts.scale ?? 1;

    this.container = this.scene.add.container(opts.x, opts.y);
    if (opts.depth != null) this.container.setDepth(opts.depth);
    this.container.setScale(this.displayScale);
    this.rebuild();
  }

  get x(): number {
    return this.container.x;
  }
  set x(v: number) {
    this.container.x = v;
  }
  get y(): number {
    return this.container.y;
  }
  set y(v: number) {
    this.container.y = v;
  }

  getOutfitSlots(): OutfitSlots {
    return { ...this.slots };
  }

  getSwimSlots(): SwimSlots {
    return { ...this.swimSlots };
  }

  getMode(): 'normal' | 'swim' {
    return this.mode;
  }

  getBreed(): string {
    return this.breed;
  }

  getPose(): string {
    return this.pose;
  }

  setBreed(breed: string, color?: string): void {
    this.breed = breed;
    if (color) this.colorHex = color;
    else if (DOGS[breed as DogBreedId]) {
      this.colorHex = DOGS[breed as DogBreedId].defaultColor;
    }
    this.rebuild();
  }

  setMode(mode: 'normal' | 'swim'): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.rebuild();
  }

  setSlots(slots: OutfitSlots): void {
    this.slots = { ...slots };
    if (this.mode === 'normal') this.rebuild();
  }

  setSwimSlots(slots: SwimSlots): void {
    this.swimSlots = { ...slots };
    if (this.mode === 'swim') this.rebuild();
  }

  /** Alias §2.2 — strój dzienny + opcjonalnie kąpielowy. */
  setOutfit(slots: OutfitSlots, swimSlots?: SwimSlots): void {
    this.slots = { ...slots };
    if (swimSlots) this.swimSlots = { ...swimSlots };
    this.rebuild();
  }

  /**
   * Ustaw klatkę bazy (idle/walkA/sit/…).
   * Brak PNG → resolveBaseTexture robi fallback (idle/procedural).
   */
  setPose(pose: string): void {
    if (this.pose === pose) {
      this.applyLayout();
      return;
    }
    this.pose = pose;
    this.rebuild();
  }

  /**
   * Przestaw warstwy wg kotwic bieżącej pozy + flip.
   * null anchors → klasyczne layerY (V1).
   */
  layoutWithAnchors(anchors: AnchorPose | null, flipX = false): void {
    this.anchors = anchors;
    this.flipX = flipX;
    this.applyLayout();
  }

  setFacingLeft(flipX: boolean): void {
    if (this.flipX === flipX) return;
    this.flipX = flipX;
    this.applyLayout();
  }

  /** Załóż / zdejmij item w slocie (live preview). */
  equip(slot: keyof OutfitSlots | keyof SwimSlots, itemId: string | null): void {
    if (slot === 'suit' || slot === 'cap') {
      this.swimSlots = { ...this.swimSlots, [slot]: itemId };
      if (this.mode === 'swim') this.rebuild();
      return;
    }
    this.slots = { ...this.slots, [slot]: itemId };
    if (this.mode === 'normal') this.rebuild();
  }

  /** Warstwy z parseOutfit (do testów / debug). */
  getLayers(): AvatarLayer[] {
    return this.computeLayers();
  }

  private computeLayers(): AvatarLayer[] {
    const baseKey = this.resolveBaseTexture();
    return parseOutfit({
      mode: this.mode,
      outfit: this.slots,
      swim: this.swimSlots,
      baseTextureKey: baseKey,
    });
  }

  private resolveBaseTexture(): string {
    const poseKey = `dog_${this.breed}_${this.pose}`;
    if (this.scene.textures.exists(poseKey)) return poseKey;
    const idleKey = `dog_${this.breed}_idle`;
    if (this.scene.textures.exists(idleKey)) return idleKey;
    if (this.scene.textures.exists('ph_dog')) return 'ph_dog';
    if (this.scene.textures.exists('dog')) return 'dog';
    return '__avatar_base_fallback__';
  }

  private rebuild(): void {
    for (const node of this.layerNodes.values()) {
      node.destroy();
    }
    this.layerNodes.clear();
    this.container.removeAll(true);

    const layers = this.computeLayers();
    for (const layer of layers) {
      const node = this.makeLayerNode(layer);
      if (!node) continue;
      this.container.add(node);
      this.layerNodes.set(layer.slot, node);
    }
    this.applyLayout();
  }

  private applyLayout(): void {
    const showShoes = shoesVisibleForPose(this.pose);
    for (const [slot, node] of this.layerNodes) {
      if (slot === 'base') {
        this.layoutBase(node);
        continue;
      }
      if (slot === 'shoes' && !showShoes) {
        setNodeVisible(node, false);
        continue;
      }
      setNodeVisible(node, true);

      if (this.anchors) {
        const anchorKey = SLOT_ANCHOR[slot] ?? 'back';
        const local = anchorToLocal(this.anchors, anchorKey, this.flipX);
        const off = slotLocalOffset(slot);
        const ox = this.flipX ? -off.x : off.x;
        setNodePosition(node, local.x + ox, local.y + off.y);
        if (this.anchors.scale !== 1 && hasSetScale(node)) {
          // skala itemu względna — display size zostaje, lekki scale na węźle
          (node as Phaser.GameObjects.Image).setScale?.(this.anchors.scale);
        }
      } else {
        setNodePosition(node, this.flipX ? 0 : 0, layerY(slot));
        // klasyczny layout V1 — X=0
        if (this.flipX) {
          // lustrzane odbicie przez scaleX na kontenerze bazy; itemy zostają
        }
      }
    }
  }

  private layoutBase(node: Phaser.GameObjects.GameObject): void {
    setNodePosition(node, 0, 0);
    if (node instanceof Phaser.GameObjects.Image) {
      node.setFlipX(this.flipX);
    } else if (node instanceof Phaser.GameObjects.Container) {
      node.setScale(this.flipX ? -1 : 1, 1);
    }
  }

  private makeLayerNode(
    layer: AvatarLayer,
  ): Phaser.GameObjects.GameObject | null {
    if (layer.slot === 'base') {
      return this.makeBase();
    }
    if (!layer.itemId) return null;
    // PNG z Preload LUB proceduralna tekstura Toca (zawsze widoczne ubranko)
    const tex = ensureItemTexture(this.scene, layer.itemId);
    if (this.scene.textures.exists(tex)) {
      const img = this.scene.add.image(0, layerY(layer.slot), tex);
      img.setDisplaySize(layerSize(layer.slot).w, layerSize(layer.slot).h);
      img.setName(layer.slot);
      img.setData('itemId', layer.itemId);
      return img;
    }
    // awaryjny fallback (gdy generateTexture niedostępny w teście bez WebGL)
    const item = ITEM_BY_ID.get(layer.itemId);
    return this.makePlaceholderItem(layer.slot, item ?? null, layer.itemId);
  }

  private makeBase(): Phaser.GameObjects.GameObject {
    const key = this.resolveBaseTexture();
    if (key !== '__avatar_base_fallback__' && this.scene.textures.exists(key)) {
      const img = this.scene.add.image(0, 0, key);
      img.setDisplaySize(140, 140);
      img.setTint(parseColorHex(this.colorHex, 0xfff5ee));
      img.setName('base');
      img.setFlipX(this.flipX);
      return img;
    }
    // proceduralny piesek (brak placeholder assetu)
    const fur = parseColorHex(this.colorHex, 0xfff5ee);
    const body = this.scene.add.ellipse(0, 8, 100, 90, fur).setStrokeStyle(4, 0x4a3f55);
    const head = this.scene.add.circle(0, -40, 38, fur).setStrokeStyle(4, 0x4a3f55);
    const earL = this.scene.add.ellipse(-28, -62, 22, 30, fur).setStrokeStyle(3, 0x4a3f55);
    const earR = this.scene.add.ellipse(28, -62, 22, 30, fur).setStrokeStyle(3, 0x4a3f55);
    const eyeL = this.scene.add.circle(-12, -42, 5, 0x4a3f55);
    const eyeR = this.scene.add.circle(12, -42, 5, 0x4a3f55);
    const nose = this.scene.add.circle(0, -30, 6, 0x4a3f55);
    const g = this.scene.add.container(0, 0, [
      earL,
      earR,
      body,
      head,
      eyeL,
      eyeR,
      nose,
    ]);
    g.setName('base');
    if (this.flipX) g.setScale(-1, 1);
    return g;
  }

  /**
   * Awaryjny placeholder bez tekstu (Toca: kształt koloru slota).
   * Normalna ścieżka: ensureItemTexture → Image.
   */
  private makePlaceholderItem(
    slot: string,
    item: ItemDef | null,
    itemId: string,
  ): Phaser.GameObjects.GameObject {
    void item;
    const color = SLOT_COLORS[slot] ?? 0xffb6c1;
    const { w, h } = layerSize(slot);
    const y = layerY(slot);
    // czysty kształt — zero napisów / emoji / „?"
    let shape: Phaser.GameObjects.GameObject;
    if (slot === 'hat' || slot === 'cap') {
      shape = this.scene.add.ellipse(0, y, w, h, color).setStrokeStyle(3, 0xffffff);
    } else if (slot === 'bow') {
      shape = this.scene.add.circle(0, y, Math.min(w, h) / 2, color).setStrokeStyle(3, 0xffffff);
    } else if (slot === 'glasses') {
      shape = this.scene.add.rectangle(0, y, w, h * 0.7, color, 0.9).setStrokeStyle(3, 0xffffff);
    } else if (slot === 'collar') {
      shape = this.scene.add.ellipse(0, y, w, h, color).setStrokeStyle(3, 0xffffff);
    } else if (slot === 'shoes') {
      shape = this.scene.add.ellipse(0, y, w, h, color).setStrokeStyle(3, 0xffffff);
    } else if (slot === 'accessory') {
      shape = this.scene.add.circle(0, y, Math.min(w, h) / 2, color).setStrokeStyle(3, 0xffffff);
    } else {
      shape = this.scene.add
        .rectangle(0, y, w, h, color, 0.95)
        .setStrokeStyle(3, 0xffffff);
    }
    const c = this.scene.add.container(0, 0, [shape]);
    c.setName(slot);
    c.setData('itemId', itemId);
    return c;
  }

  destroy(): void {
    this.container.destroy(true);
    this.layerNodes.clear();
  }
}

function setNodePosition(node: Phaser.GameObjects.GameObject, x: number, y: number): void {
  const n = node as unknown as { setPosition?: (x: number, y: number) => void; x?: number; y?: number };
  if (typeof n.setPosition === 'function') n.setPosition(x, y);
  else {
    n.x = x;
    n.y = y;
  }
}

function setNodeVisible(node: Phaser.GameObjects.GameObject, visible: boolean): void {
  const n = node as unknown as { setVisible?: (v: boolean) => void };
  if (typeof n.setVisible === 'function') n.setVisible(visible);
}

function hasSetScale(node: Phaser.GameObjects.GameObject): boolean {
  return typeof (node as unknown as { setScale?: unknown }).setScale === 'function';
}

function layerY(slot: string): number {
  switch (slot) {
    case 'hat':
    case 'cap':
      return -58;
    case 'bow':
      return -48;
    case 'glasses':
      return -36;
    case 'collar':
      return 8;
    case 'body':
    case 'suit':
      return 10;
    case 'shoes':
      return 52;
    case 'accessory':
      return 20;
    default:
      return 0;
  }
}

function layerSize(slot: string): { w: number; h: number } {
  switch (slot) {
    case 'hat':
    case 'cap':
      return { w: 70, h: 36 };
    case 'bow':
      return { w: 40, h: 28 };
    case 'glasses':
      return { w: 56, h: 18 };
    case 'collar':
      // szersza/wyższa — widać pasek + breloczek na szyi
      return { w: 78, h: 36 };
    case 'body':
    case 'suit':
      return { w: 90, h: 70 };
    case 'shoes':
      return { w: 80, h: 22 };
    case 'accessory':
      return { w: 36, h: 36 };
    default:
      return { w: 48, h: 48 };
  }
}

export { itemTextureKey };
